import { useState, useEffect, useRef } from 'react';

interface TextSelection {
  text: string;
  startOffset: number;
  endOffset: number;
  isValid: boolean;
}

interface UseTextSelectionReturn {
  selectedText: string;
  selectionInfo: TextSelection | null;
  showButton: boolean;
  buttonPosition: { x: number; y: number };
  clearSelection: () => void;
}

export const useTextSelection = (containerRef: React.RefObject<HTMLElement>): UseTextSelectionReturn => {
  const [selectedText, setSelectedText] = useState('');
  const [selectionInfo, setSelectionInfo] = useState<TextSelection | null>(null);
  const [showButton, setShowButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });

  const clearSelection = () => {
    setSelectedText('');
    setSelectionInfo(null);
    setShowButton(false);
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      // Small delay to ensure selection is complete
      setTimeout(() => {
        const selection = window.getSelection();
        
        if (!selection || selection.rangeCount === 0) {
          if (showButton) {
            setShowButton(false);
          }
          return;
        }

        const range = selection.getRangeAt(0);
        const text = selection.toString().trim();

        // Check if selection is within our container
        if (containerRef.current && !containerRef.current.contains(range.commonAncestorContainer)) {
          if (showButton) {
            setShowButton(false);
          }
          return;
        }

        if (text.length > 3) { // Only show button for meaningful selections
          const rect = range.getBoundingClientRect();
          
          setSelectedText(text);
          setSelectionInfo({
            text,
            startOffset: range.startOffset,
            endOffset: range.endOffset,
            isValid: text.length >= 3 && text.length <= 500
          });
          
          // Get container position for relative positioning
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (!containerRect) return;
          
          // Calculate position relative to container
          const relativeX = rect.left - containerRect.left + (rect.width / 2);
          const relativeY = rect.top - containerRect.top - 60; // 60px above selection
          
          // Constrain to container bounds
          const containerWidth = containerRect.width;
          const buttonWidth = 200;
          const minX = buttonWidth / 2;
          const maxX = containerWidth - buttonWidth / 2;
          
          const constrainedX = Math.max(minX, Math.min(maxX, relativeX));
          const constrainedY = Math.max(-50, relativeY); // Allow some negative space above container
          
          setButtonPosition({
            x: constrainedX,
            y: constrainedY
          });
          
          setShowButton(true);
        } else if (showButton) {
          setShowButton(false);
        }
      }, 150); // Increased delay to allow selection to complete
    };

    const handleMouseUp = (event: MouseEvent) => {
      // Only handle if within our container
      if (containerRef.current && containerRef.current.contains(event.target as Node)) {
        handleSelectionChange();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Handle text selection with keyboard (Shift + arrows)
      if (event.shiftKey && (
          event.key === 'ArrowLeft' || event.key === 'ArrowRight' || 
          event.key === 'ArrowUp' || event.key === 'ArrowDown'
        )) {
        handleSelectionChange();
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      // If clicking outside our container, clear selection
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (showButton) {
          clearSelection();
        }
      }
    };

    // Use document events for broader coverage
    document.addEventListener('mouseup', handleMouseUp, { passive: true });
    document.addEventListener('keyup', handleKeyUp, { passive: true });
    document.addEventListener('mousedown', handleMouseDown, { passive: true });

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [containerRef, showButton]);

  return {
    selectedText,
    selectionInfo,
    showButton,
    buttonPosition,
    clearSelection
  };
}; 